import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gamesAPI, seasonsAPI } from '../api/api';

const GAMES_PER_PAGE = 20;

const AllGames = () => {
  const { t } = useTranslation();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeSeason, setActiveSeason] = useState(null);
  const [error, setError] = useState(null);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const debounceTimer = useRef(null);

  // Fetch active season
  useEffect(() => {
    const fetchSeason = async () => {
      try {
        const res = await seasonsAPI.getActive();
        setActiveSeason(res.data);
      } catch (err) {
        console.error('Error fetching season:', err);
      }
    };
    fetchSeason();
  }, []);

  // Debounce search input
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(debounceTimer.current);
  }, [search]);

  // Reset when search changes
  useEffect(() => {
    setGames([]);
    setPage(1);
    setTotalPages(1);
  }, [debouncedSearch]);

  // Fetch games
  const fetchGames = useCallback(async (pageNum, isLoadMore = false) => {
    if (!activeSeason) return;
    
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const params = {
        seasonId: activeSeason.id,
        played: 'true',
        page: pageNum,
        limit: GAMES_PER_PAGE
      };
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      const res = await gamesAPI.getAll(params);
      const data = res.data;

      if (data.games) {
        if (isLoadMore) {
          setGames(prev => [...prev, ...data.games]);
        } else {
          setGames(data.games);
        }
        setTotalPages(data.totalPages);
      }
    } catch (err) {
      console.error('Error fetching games:', err);
      setError(t('common.error'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeSeason, debouncedSearch, t]);

  // Initial load and search changes
  useEffect(() => {
    if (activeSeason) {
      fetchGames(1, false);
    }
  }, [activeSeason, debouncedSearch, fetchGames]);

  // Load more when page changes (beyond first page)
  useEffect(() => {
    if (page > 1 && activeSeason) {
      fetchGames(page, true);
    }
  }, [page, activeSeason, fetchGames]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && page < totalPages) {
          setPage(prev => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [loadingMore, page, totalPages]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-200">
          {t('allGames.title')}
          {activeSeason && (
            <span className="text-lg md:text-xl font-normal text-gray-600 dark:text-gray-400 ml-2">
              - {activeSeason.name}
            </span>
          )}
        </h1>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('allGames.searchPlaceholder')}
            className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base dark:bg-gray-700 dark:text-white dark:border-gray-600"
          />
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-400"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 text-red-600 dark:text-red-400 p-4 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Games List */}
      {!loading && games.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {debouncedSearch ? t('allGames.noResults') : t('allGames.noGames')}
        </div>
      )}

      {!loading && games.length > 0 && (
        <div className="space-y-3">
          {games.map((game) => (
            <Link
              key={game.id}
              to={`/game/${game.id}`}
              className="block bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md dark:shadow-gray-900 transition-shadow border border-gray-100 dark:border-gray-700 px-4 py-3"
            >
              <div className="flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-sm md:text-base truncate ${game.awayScore > game.homeScore ? 'text-green-700 dark:text-green-400' : 'text-gray-800 dark:text-gray-200'}`}>
                    {game.awayTeam?.name || 'TBD'}
                  </div>
                  <div className={`font-semibold text-sm md:text-base truncate ${game.homeScore > game.awayScore ? 'text-green-700 dark:text-green-400' : 'text-gray-800 dark:text-gray-200'}`}>
                    {game.homeTeam?.name || 'TBD'}
                  </div>
                </div>
                <div className="text-right ml-4 flex-shrink-0">
                  <div className={`text-lg md:text-xl font-bold ${game.awayScore > game.homeScore ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                    {game.awayScore}
                  </div>
                  <div className={`text-lg md:text-xl font-bold ${game.homeScore > game.awayScore ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                    {game.homeScore}
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex justify-between">
                <span>{new Date(game.gameDate).toLocaleDateString()}</span>
                <span>{t('home.clickForBoxScore')}</span>
              </div>
            </Link>
          ))}

          {/* Infinite scroll trigger */}
          {page < totalPages && (
            <div ref={loadMoreRef} className="flex justify-center py-6">
              {loadingMore && (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AllGames;
